import { useEffect, useState } from "react";
import api, { pickErr } from "@/lib/api";

function StatCard({ label, value, testid, suffix = "" }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-5"
      data-testid={testid}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-slate-900 tabular-nums">
        {value ?? 0}
        {suffix ? <span className="text-base ml-1">{suffix}</span> : null}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: body } = await api.get("/school/analytics");
        if (!cancelled) setData(body?.data || null);
      } catch (err) {
        if (!cancelled) setError(pickErr(err, "Failed to load analytics"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const classes = data?.class_summary || [];

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Analytics</h2>
      <p className="text-sm text-slate-500 mb-6">
        Totals for your school only.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p data-testid="school-analytics-error" className="text-sm text-red-600">
          {error}
        </p>
      ) : data ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total students"
              value={data.total_students}
              testid="school-analytics-students"
            />
            <StatCard
              label="Tests assigned"
              value={data.tests_assigned}
              testid="school-analytics-assigned"
            />
            <StatCard
              label="Tests completed"
              value={data.tests_completed}
              testid="school-analytics-completed"
            />
            <StatCard
              label="Completion rate"
              value={data.completion_rate}
              suffix="%"
              testid="school-analytics-rate"
            />
          </div>

          <h3 className="mt-8 mb-3 text-sm font-semibold text-slate-900">
            Completions by assessment
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Personality completed"
              value={data.personality_completed}
              testid="school-analytics-personality"
            />
            <StatCard
              label="RIASEC completed"
              value={data.riasec_completed}
              testid="school-analytics-riasec"
            />
            <StatCard
              label="Aptitude completed"
              value={data.aptitude_completed}
              testid="school-analytics-aptitude"
            />
            <StatCard
              label="Well-being completed"
              value={data.wellbeing_completed}
              testid="school-analytics-wellbeing"
            />
            <StatCard
              label="Pending students"
              value={data.pending_students}
              testid="school-analytics-pending"
            />
          </div>

          {classes.length > 0 ? (
            <>
              <h3 className="mt-8 mb-3 text-sm font-semibold text-slate-900">
                Class-wise completion
              </h3>
              <div
                className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                data-testid="school-analytics-class-table"
              >
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-2 font-medium">Class</th>
                      <th className="px-4 py-2 font-medium text-right">
                        Students
                      </th>
                      <th className="px-4 py-2 font-medium text-right">
                        Completed any
                      </th>
                      <th className="px-4 py-2 font-medium text-right">
                        Pending
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((c) => {
                      const pending = (c.students || 0) - (c.completed_any || 0);
                      return (
                        <tr
                          key={c.class_name}
                          className="border-t border-slate-100"
                          data-testid={`school-analytics-class-${c.class_name}`}
                        >
                          <td className="px-4 py-2 text-slate-900">
                            {c.class_name}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                            {c.students}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-emerald-700">
                            {c.completed_any}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                            {pending}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
